/**
 * Billing, as far as the browser is concerned.
 *
 * Paddle is the merchant of record. Nothing here knows a price, a currency or
 * a total: the workspace asks the backend to open a transaction for a *plan*,
 * and Paddle's own overlay states what is being charged and takes the money.
 * That is the point of a merchant of record, and it is also why a price never
 * travels from this file — a client that could name a price could name a
 * cheaper one.
 */
import { apiGet, apiPost, type ApiFailure } from "./api-client";

export interface Subscription {
  status: string;
  /** The one field the rest of the product cares about. */
  grantsAccess: boolean;
  priceId: string | null;
  currentPeriodEnd: string | null;
}

interface SubscriptionResponse {
  status: string;
  grants_access: boolean;
  price_id: string | null;
  current_period_end: string | null;
}

export interface BillingState {
  plans: string[];
  subscription: Subscription | null;
  /** Paddle's public browser configuration, served at runtime. */
  clientToken: string | null;
  environment: string;
  failure: ApiFailure | null;
}

interface PlansResponse {
  plans: string[];
  client_token: string | null;
  environment: string;
}

function toSubscription(body: SubscriptionResponse): Subscription {
  return {
    status: body.status,
    grantsAccess: body.grants_access,
    priceId: body.price_id,
    currentPeriodEnd: body.current_period_end,
  };
}

/**
 * Everything the billing page needs, in one round trip pair.
 *
 * A failure on either call is reported rather than thrown: a billing page that
 * cannot reach the backend should say so, not disappear into an error
 * boundary that tells the reader nothing about their subscription.
 */
export async function readBilling(): Promise<BillingState> {
  const [plans, subscription] = await Promise.all([
    apiGet<PlansResponse>("/api/v1/billing/plans"),
    apiGet<SubscriptionResponse | null>("/api/v1/billing/subscription"),
  ]);

  if (!plans.ok) {
    return {
      plans: [],
      subscription: null,
      clientToken: null,
      environment: "sandbox",
      failure: plans.failure,
    };
  }

  const configuration = {
    plans: plans.data.plans,
    clientToken: plans.data.client_token,
    environment: plans.data.environment,
  };

  if (!subscription.ok) {
    return { ...configuration, subscription: null, failure: subscription.failure };
  }

  return {
    ...configuration,
    subscription: subscription.data ? toSubscription(subscription.data) : null,
    failure: null,
  };
}

/** Open a transaction for a plan. The id is what Paddle's overlay opens. */
export async function startCheckout(
  plan: string,
): Promise<{ ok: true; transactionId: string } | { ok: false; failure: ApiFailure }> {
  const result = await apiPost<{ transaction_id: string }>("/api/v1/billing/checkout", {
    plan,
  });
  return result.ok
    ? { ok: true, transactionId: result.data.transaction_id }
    : { ok: false, failure: result.failure };
}

/** A URL where the customer manages their own plan, at Paddle. */
export async function openPortal(): Promise<
  { ok: true; url: string } | { ok: false; failure: ApiFailure }
> {
  const result = await apiPost<{ url: string }>("/api/v1/billing/portal");
  return result.ok ? { ok: true, url: result.data.url } : { ok: false, failure: result.failure };
}
